import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BeforeAfterGallery = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(0);
  const [viewMode, setViewMode] = useState('before'); // 'before', 'after', 'split'

  const currentProject = projects?.[selectedProject];

  const nextProject = () => {
    setSelectedProject((prev) => (prev + 1) % projects?.length);
  };

  const prevProject = () => {
    setSelectedProject((prev) => (prev - 1 + projects?.length) % projects?.length);
  };

  return (
    <div className="bg-white rounded-xl shadow-elevation-2 p-8 border border-border">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Before & After Gallery
        </h2>
        <p className="text-text-secondary">
          See the amazing transformations we've achieved for our customers
        </p>
      </div>
      {/* View Mode Toggle */}
      <div className="flex justify-center mb-6">
        <div className="bg-muted rounded-lg p-1 flex">
          <button
            onClick={() => setViewMode('before')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              viewMode === 'before'
                ? 'bg-white text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Before
          </button>
          <button
            onClick={() => setViewMode('split')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              viewMode === 'split' ?'bg-white text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Split View
          </button>
          <button
            onClick={() => setViewMode('after')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              viewMode === 'after' ?'bg-white text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            After
          </button>
        </div>
      </div>
      {/* Main Gallery */}
      <div className="relative mb-6">
        <div className="relative rounded-lg overflow-hidden bg-muted" style={{ height: '400px' }}>
          {viewMode === 'before' && (
            <Image
              src={currentProject?.beforeImage}
              alt={`Before - ${currentProject?.title}`}
              className="w-full h-full object-cover"
            />
          )}
          
          {viewMode === 'after' && (
            <Image
              src={currentProject?.afterImage}
              alt={`After - ${currentProject?.title}`}
              className="w-full h-full object-cover"
            />
          )}
          
          {viewMode === 'split' && (
            <div className="flex h-full">
              <div className="w-1/2 relative">
                <Image
                  src={currentProject?.beforeImage}
                  alt={`Before - ${currentProject?.title}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Before
                </div>
              </div>
              <div className="w-1/2 relative">
                <Image
                  src={currentProject?.afterImage}
                  alt={`After - ${currentProject?.title}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                  After
                </div>
              </div>
              <div className="absolute inset-y-0 left-1/2 w-1 bg-white transform -translate-x-1/2" />
            </div>
          )}
          
          {/* Navigation Arrows */}
          <button
            onClick={prevProject}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-elevation-2 transition-all duration-200"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          
          <button
            onClick={nextProject}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-elevation-2 transition-all duration-200"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>
        
        {/* Project Info Overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
            <h3 className="font-semibold text-text-primary mb-1">
              {currentProject?.title}
            </h3>
            <p className="text-sm text-text-secondary mb-2">
              {currentProject?.description}
            </p>
            <div className="flex items-center space-x-4 text-xs text-text-secondary">
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={12} />
                <span>{currentProject?.location}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={12} />
                <span>{currentProject?.date}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>{currentProject?.duration}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Thumbnail Navigation */}
      <div className="flex space-x-3 overflow-x-auto pb-2">
        {projects?.map((project, index) => (
          <button
            key={index}
            onClick={() => setSelectedProject(index)}
            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
              selectedProject === index
                ? 'border-primary shadow-elevation-2'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <Image
              src={project?.afterImage}
              alt={project?.title}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
      {/* Call to Action */}
      <div className="mt-8 text-center">
        <p className="text-text-secondary mb-4">
          Ready to transform your space like these amazing results?
        </p>
        <Button
          variant="default"
          iconName="Calendar"
          iconPosition="left"
        >
          Book Your Service Today
        </Button>
      </div>
    </div>
  );
};

export default BeforeAfterGallery;